import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import type {
  UserInterface,
  UserUpdateDtoInterface,
} from '../models/user.interface';
import type { ChangePasswordReponseInterface } from '../models/changePassword.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(private readonly apollo: Apollo) { }

  searchUsers(searchText: string, clinicId: number): Observable<UserInterface[]> {

    return this.apollo.query<SearchUsersReponse>({
      query: gql`query searchUsers($searchText : String!, $clinicId : Long!) {
        searchUsers(clinicId : $clinicId, order: { lastName: ASC }, where : { or : [ { firstName : {contains : $searchText } },{ lastName : {contains : $searchText } }, { email : {contains : $searchText } } ]})
        {
          id
          firstName
          lastName
          email
        }
      }`,
      variables: { searchText, clinicId }
    }).pipe(map(res => res.data.searchUsers));

  }

  searchExternalUsers(searchText: string, clinicId: number): Observable<UserInterface[]> {

    return this.apollo.query<SearchExternalUsersReponse>({
      query: gql`query searchExternalUsers($searchText : String!, $clinicId : Long!) {
        searchExternalUsers(clinicId : $clinicId, searchText : $searchText, order: { lastName: ASC })
        {
          id
          firstName
          lastName
          email
        }
      }`,
      variables: { searchText, clinicId }
    }).pipe(map(res => res.data.searchExternalUsers));

  }

  getUserConnected(): Observable<UserInterface> {
    return this.apollo.query<{ me: UserInterface }>({
      query: gql`query getMe {
        me {
          id
          firstName
          lastName
          email
          locale
          signature
        }
      }`
    }).pipe(map(res => res.data?.me ));
  }

  updateUserConnected(dto: UserUpdateDtoInterface): Observable<UserInterface | undefined> {
    return this.apollo.mutate<{ updateUserConnected: UserInterface }>({
      mutation: gql`mutation updateUserConnected($dto: UserUpdateDtoInput!) {
        updateUserConnected(dto: $dto) {
          id
          firstName
          lastName
          email
          locale
          signature
        }
      }`,
      variables: { dto },
      refetchQueries: ['getPreferences']
    }).pipe(map(res => res.data?.updateUserConnected));
  }

  updateUserSelectedClinic(userId: number, clinicId: number): Observable<UserInterface | undefined> {
    return this.apollo.mutate<{ updateUserSelectedClinic: UserInterface }>({
      mutation: gql`mutation updateUserSelectedClinic($userId: Long!, $clinicId : Long!) {
        updateUserSelectedClinic(userId: $userId, clinicId: $clinicId) {
          id
          firstName
          lastName
          email
          selectedClinicId
        }
      }`,
      variables: { userId, clinicId }
    }).pipe(map(res => res.data?.updateUserSelectedClinic));
  }


  changePassword(currentPassword: string, newPassword: string): Observable<ChangePasswordReponseInterface | undefined> {
    return this.apollo.mutate<{ changePassword: ChangePasswordReponseInterface }>({
      mutation: gql`mutation changePassword($currentPassword: String, $newPassword: String) {
        changePassword(currentPassword: $currentPassword, newPassword: $newPassword) {
          isSuccess
          errors
        }
      }`,
      variables: { currentPassword, newPassword }
    }).pipe(map(res => res.data?.changePassword));
  }

  deleteMyAccount(): Observable<boolean | undefined> {
    return this.apollo.mutate<{ deleteMyAccount: boolean }>({
      mutation: gql`mutation deleteMyAccount {
        deleteMyAccount
      }`
    }).pipe(map(res => res.data?.deleteMyAccount));
  }
}

export interface SearchUsersReponse {
  searchUsers: UserInterface[];
}

export interface SearchExternalUsersReponse {
  searchExternalUsers: UserInterface[];
}
